import { useEffect, useLayoutEffect, useRef, type CSSProperties, type ReactNode } from 'react';
import styles from './HomeFeedbackArea.module.css';

/**
 * One bounded feedback area on home (garden-remember readiness P1,
 * 2026-10-15): delete Undo, protection messages and the existing recovery UI
 * (InlineSaveRescueBanner, ContentLifecycleNotice) share it instead of
 * overlapping as separate absolute positions.
 *
 * - Pinned row: only Undo (DeletionNotices portals it into `undoSlotRef`).
 * - One scroll column, in priority order: a failure message (head slot), the
 *   recovery UI passed as children, a pending / preparing message (tail slot).
 * - Bounded: starts 20 px below the header circles and ends 8 px above the
 *   dock, the Clear pill, or the keyboard (`limitPx`, from PrayPage's shared
 *   geometry). Opaque backing only while it holds something.
 *
 * A failure that arrives while the column is scrolled is brought into view
 * (scrollTop 0), unless the user is interacting with the column: focus inside
 * it, a pointer pressed on it, or a scroll in the last second. Focus never
 * moves. While a failure sits above the visible part, a red edge and top fade
 * mark it. When the head slot grows or shrinks without that reveal (column
 * scrolled, or the user interacting with it even at position 0), the
 * column's scroll position is compensated so the content being read does not
 * jump (browser scroll anchoring is off: WebKit has none, so both engines use
 * this one rule). Presentation only: no notice state lives here.
 */

const RECENT_SCROLL_MS = 1000;

interface HomeFeedbackAreaProps {
  label: string;
  /** Visible bottom of the home container (visualViewport-aware), px. */
  visibleHeight: number;
  /** Lower bound from the container top when the Clear pill or keyboard sets it. */
  limitPx: number | null;
  /** Container px the app notice stack reaches down to (0 when it is empty). */
  noticeClearancePx?: number;
  undoSlotRef: (element: HTMLDivElement | null) => void;
  headSlotRef: (element: HTMLDivElement | null) => void;
  tailSlotRef: (element: HTMLDivElement | null) => void;
  children?: ReactNode;
}

export default function HomeFeedbackArea({
  label,
  visibleHeight,
  limitPx,
  noticeClearancePx = 0,
  undoSlotRef,
  headSlotRef,
  tailSlotRef,
  children,
}: HomeFeedbackAreaProps) {
  const areaRef = useRef<HTMLDivElement>(null);
  const columnRef = useRef<HTMLDivElement>(null);
  const labelRef = useRef(label);

  useEffect(() => {
    labelRef.current = label;
    const column = columnRef.current;
    if (column?.hasAttribute('role')) column.setAttribute('aria-label', label);
  }, [label]);

  useLayoutEffect(() => {
    const area = areaRef.current;
    const pinned = area?.querySelector<HTMLElement>(':scope > [data-slot="undo"]');
    const column = columnRef.current;
    if (!area || !pinned || !column) return;
    let pointerDown = false;
    let lastUserScroll = -Infinity;
    let programmatic = false;
    let seenFailure: string | null = null;
    const head = column.querySelector<HTMLElement>(':scope > [data-slot="head"]');
    // The space the head slot takes, including the column gap it adds once it
    // holds something (an empty slot is display: none).
    const headExtent = () => {
      if (!head || head.offsetHeight === 0) return 0;
      return head.offsetHeight + (parseFloat(getComputedStyle(column).rowGap) || 0);
    };
    let headHeight = headExtent();

    const toggle = (element: HTMLElement, name: string, on: boolean) => {
      if (on) element.setAttribute(name, '');
      else element.removeAttribute(name);
    };
    const interacting = () =>
      column.contains(document.activeElement) ||
      pointerDown ||
      performance.now() - lastUserScroll < RECENT_SCROLL_MS;

    const update = () => {
      const pinnedFilled = pinned.querySelector('[data-notice]') !== null;
      const columnFilled = column.querySelector(':scope > :not([data-slot]), :scope > [data-slot] > *') !== null;
      toggle(area, 'data-filled', pinnedFilled || columnFilled);
      toggle(area, 'data-pinned', pinnedFilled);
      toggle(area, 'data-column', columnFilled);
      if (columnFilled) {
        column.setAttribute('role', 'region');
        column.setAttribute('aria-label', labelRef.current);
      } else {
        column.removeAttribute('role');
        column.removeAttribute('aria-label');
      }
      const scrollable = column.scrollHeight > column.clientHeight + 1;
      // A scrollable region with text must be reachable by keyboard.
      if (scrollable) column.tabIndex = 0;
      else column.removeAttribute('tabindex');
      toggle(column, 'data-more-below', column.scrollTop + column.clientHeight < column.scrollHeight - 1);
      const failure = column.querySelector<HTMLElement>('[data-notice="failure"]');
      const event = failure?.dataset.noticeEvent ?? null;
      if (event !== null && event !== seenFailure && !interacting() && column.scrollTop !== 0) {
        programmatic = true;
        column.scrollTop = 0;
      }
      seenFailure = event;
      const above = !!failure &&
        failure.getBoundingClientRect().top < column.getBoundingClientRect().top - 1;
      toggle(column, 'data-failure-above', above);
    };

    let frame = 0;
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(() => { frame = 0; update(); });
    };
    // Keep the content in view still while the head slot changes size (a
    // failure arriving or leaving above what the user is reading): always when
    // the column is scrolled, and at scroll position 0 too while the user is
    // interacting with it. Not interacting at 0 is the reveal case: the
    // failure is meant to push in at the top and be seen.
    const headSizes = new ResizeObserver(() => {
      if (!head) return;
      const next = headExtent();
      const delta = next - headHeight;
      headHeight = next;
      if (delta !== 0 && (column.scrollTop > 0 || interacting())) {
        programmatic = true;
        column.scrollTop += delta;
      }
      schedule();
    });
    if (head) headSizes.observe(head);
    const sizes = new ResizeObserver(schedule);
    const observeSizes = () => {
      sizes.disconnect();
      sizes.observe(column);
      for (const child of column.querySelectorAll(':scope > *, :scope > [data-slot] > *')) sizes.observe(child);
    };
    const mutations = new MutationObserver(() => {
      observeSizes();
      update();
    });
    mutations.observe(area, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-notice-event'] });
    const onScroll = () => {
      if (programmatic) programmatic = false;
      else lastUserScroll = performance.now();
      schedule();
    };
    const onPointerDown = () => { pointerDown = true; };
    const onPointerUp = () => { pointerDown = false; };
    column.addEventListener('scroll', onScroll, { passive: true });
    column.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('pointerup', onPointerUp);
    window.addEventListener('pointercancel', onPointerUp);
    observeSizes();
    update();
    return () => {
      if (frame) cancelAnimationFrame(frame);
      sizes.disconnect();
      headSizes.disconnect();
      mutations.disconnect();
      column.removeEventListener('scroll', onScroll);
      column.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointercancel', onPointerUp);
    };
  }, []);

  const style = {
    '--home-visible-height': `${visibleHeight}px`,
    ...(limitPx !== null ? { '--home-feedback-limit': `${limitPx}px` } : {}),
    // Start below the app notice stack when it reaches further down than the
    // usual top; without notices the geometry is exactly as before.
    ...(noticeClearancePx > 0
      ? { '--home-area-top': `max(var(--home-feedback-top), ${noticeClearancePx}px)` }
      : {}),
  } as CSSProperties;

  return (
    <div
      ref={areaRef}
      className={styles.area}
      style={style}
      data-home-feedback=""
      data-limit={limitPx !== null ? '' : undefined}
    >
      <div ref={undoSlotRef} className={styles.pinned} data-slot="undo" />
      <div className={styles.columnWrap}>
        <div ref={columnRef} className={styles.column} data-home-feedback-column="">
          <div ref={headSlotRef} className={styles.slot} data-slot="head" />
          {children}
          <div ref={tailSlotRef} className={styles.slot} data-slot="tail" />
        </div>
        <span className={styles.cue} aria-hidden="true" />
      </div>
    </div>
  );
}
